import {
  Add as AddIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Chip,
  FilterOptionsState,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import { CommunicationTypeEnum, CustomerCommunication } from "../../store/types/communication.types";


interface Attendee {
  email: string;
  name?: string;
}

interface ContactOption {
  email: string;
  name: string | undefined;
  isPrimary: boolean;
  contactPersonId: number;
}

interface AttendeeSelectorProps {
  attendees: Attendee[];
  onAttendeesChange: (attendees: Attendee[]) => void;
  communications?: CustomerCommunication[];
  isLoading?: boolean;
  validationError?: string | null;
  onValidationErrorChange?: (error: string | null) => void;
}

const AttendeeSelector: React.FC<AttendeeSelectorProps> = ({
  attendees,
  onAttendeesChange,
  communications = [],
  isLoading = false,
  validationError,
  onValidationErrorChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<ContactOption | null>(
    null
  );

  // Extrahera e-postadresser från kommunikationsdata (Type 3 = email)
  const emailContacts = useMemo(() => {
    return communications
      .filter(
        (comm) =>
          comm.Type === CommunicationTypeEnum.Epost && comm.Status && comm.Value
      )
      .map((comm) => ({
        email: comm.Value!,
        name: comm.ContactPerson?.Firstname ? `${comm.ContactPerson?.Firstname} ${comm.ContactPerson?.Lastname}`.trim() : undefined,
        isPrimary: comm.IsPrimary || false,
        contactPersonId: comm.ContactPersonNo || 0,
      }))
      .sort((a, b) => {
        // Sortera så att primära kontakter kommer först
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return a.name?.localeCompare(b.name || "") || 0;
      });
  }, [communications]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addAttendee = (email: string, name?: string) => {
    if (!email) return;

    if (!validateEmail(email)) {
      onValidationErrorChange?.("Ogiltig e-postadress");
      return;
    }

    if (attendees.some((att) => att.email === email)) {
      onValidationErrorChange?.("Denna deltagare har redan lagts till");
      return;
    }

    const newAttendee: Attendee = {
      email,
      name: name || undefined,
    };

    onAttendeesChange([...attendees, newAttendee]);
    setInputValue("");
    setSelectedValue(null);
    onValidationErrorChange?.(null);
  };

  const removeAttendee = (email: string) => {
    const updatedAttendees = attendees.filter((att) => att.email !== email);
    onAttendeesChange(updatedAttendees);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedValue) {
        addAttendee(selectedValue.email, selectedValue.name);
      } else if (inputValue) {
        addAttendee(inputValue);
      }
    }
  };

  const handleAutocompleteChange = (
    _: React.SyntheticEvent,
    value: ContactOption | string | null
  ) => {
    if (typeof value === "string") {
      // Användaren skrev in en ny e-postadress
      addAttendee(value);
    } else if (value) {
      // Användaren valde från listan
      addAttendee(value.email, value.name);
    }
  };

  const getOptionLabel = (option: ContactOption | string) => {
    if (typeof option === "string") {
      return option;
    }
    return `${option.name} <${option.email}>`;
  };

  const filterOptions = (
    options: ContactOption[],
    { inputValue }: FilterOptionsState<ContactOption>
  ) => {
    const filtered = options.filter(
      (option) =>
        option.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.email.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Om input ser ut som en e-postadress och inte finns i listan, lägg till som option
    if (
      inputValue &&
      validateEmail(inputValue) &&
      !options.some(
        (opt) => opt.email.toLowerCase() === inputValue.toLowerCase()
      )
    ) {
      return [
        ...filtered,
        { email: inputValue, name: "", isPrimary: false, contactPersonId: 0 },
      ];
    }

    return filtered;
  };
  console.log(attendees)
  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography
        variant="subtitle2"
        gutterBottom
        sx={{ display: "flex", alignItems: "center" }}
      >
        <PersonIcon sx={{ mr: 1 }} /> Deltagare
      </Typography>

      <Autocomplete
        freeSolo
        options={emailContacts}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        value={selectedValue}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={handleAutocompleteChange}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Lägg till deltagare via e-post"
            fullWidth
            margin="normal"
            onKeyPress={handleKeyPress}
            error={!!validationError}
            helperText={validationError}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        if (selectedValue) {
                          addAttendee(selectedValue.email, selectedValue.name);
                        } else if (inputValue) {
                          addAttendee(inputValue);
                        }
                      }}
                      disabled={!inputValue && !selectedValue}
                      color="primary"
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          if (option.contactPersonId === 0) {
            return (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{option.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lägg till ny e-postadress
                  </Typography>
                </Box>
              </li>
            );
          }

          return (
            <li {...props}>
              <Box>
                <Typography variant="body2">
                  {option.name}
                  {option.isPrimary && (
                    <Chip
                      label="Primär"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                </Typography>
              </Box>
            </li>
          );
        }}
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {attendees.map((att) => (
          <Chip
            key={att.email}
            label={att.name || att.email}
            onDelete={() => removeAttendee(att.email)}
            deleteIcon={<CloseIcon />}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AttendeeSelector;
